import { z } from 'zod/v4';

import { SankeyColorStrategy, StudioVisualizationType } from '@blockworks/platform/api/research/studio';

import {
    BaseVisualizationArgsSchema,
    ColumnReferenceSchema,
    createBaseGroupSchema,
    createColumnTransform,
    createGroupedVisualizationSchema,
    createRowTransform,
    createVisualizationResponseSchema,
    ExecutionMetadata,
    FormatFieldSchema,
    RowIndexFieldSchema,
    VisualizationParametersSchema,
} from '../validation/schemas';

const SankeySumRangeSchema = z.object({
    from: z.number().min(0).nullable().default(null),
    to: z.number().nullable().default(null),
    enabled: z.boolean().default(false),
});

const SankeyLinkSchema = z.object({
    source: z.string().default(''),
    target: z.string().default(''),
    columnKey: ColumnReferenceSchema, // Will be transformed
    valueFormat: FormatFieldSchema,
    rowIndex: RowIndexFieldSchema, // Will be transformed
    color: z.string(),
    sumRange: SankeySumRangeSchema,
});

const SankeyGroupSchema = createBaseGroupSchema(
    z.object({
        links: z.array(SankeyLinkSchema).default([]),
    }),
);

export const SankeyOptionsSchema = createGroupedVisualizationSchema(SankeyGroupSchema, {
    nodeWidth: z.number(),
    nodePadding: z.number(),
    showSourceLabels: z.boolean().default(true),
    showTargetLabels: z.boolean().default(true),
    colorStrategy: z.nativeEnum(SankeyColorStrategy).default(SankeyColorStrategy.Target),
});

export const createSankeyArgsSchema = <TColumns extends string = string>(meta: ExecutionMetadata<TColumns>) => {
    const columnTransform = createColumnTransform(meta);
    const rowIndexTransform = createRowTransform(meta);

    return BaseVisualizationArgsSchema.extend({
        type: z.literal(StudioVisualizationType.Sankey),
        config: z.object({
            id: z.string().optional(),
            parameters: VisualizationParametersSchema.optional(),
            options: createGroupedVisualizationSchema(
                SankeyGroupSchema.extend({
                    links: z.array(
                        SankeyLinkSchema.extend({
                            rowIndex: rowIndexTransform,
                            columnKey: columnTransform,
                        }),
                    ),
                }),
                {
                    nodeWidth: z.number(),
                    nodePadding: z.number(),
                    showSourceLabels: z.boolean().default(true),
                    showTargetLabels: z.boolean().default(true),
                    colorStrategy: z.enum(SankeyColorStrategy).default(SankeyColorStrategy.Target),
                },
            ),
        }),
    });
};

export const SankeyResponseSchema = createVisualizationResponseSchema(StudioVisualizationType.Sankey).extend({
    options: SankeyOptionsSchema,
    data: z.any(),
});

export type SankeyOptions = z.infer<typeof SankeyOptionsSchema>;
export type SankeyResponse = z.infer<typeof SankeyResponseSchema>;

// Export the generic type for the args schema
export type SankeyArgsSchema<TColumns extends string = string> = ReturnType<typeof createSankeyArgsSchema<TColumns>>;
export type SankeyArgs<TColumns extends string = string> = z.infer<SankeyArgsSchema<TColumns>>;
